import { Card, Col, Empty, Row, Typography, theme as antdTheme } from 'antd';
import {
  AlertOutlined,
  BankOutlined,
  BookOutlined,
  DollarOutlined,
  FileTextOutlined,
  LineChartOutlined,
  PercentageOutlined,
  RightOutlined,
  TeamOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router';
import { PageHeader } from '@/shared/components/page-header';
import { useAbility } from '@/auth/ability-provider';
import type { Actions, Subjects } from '@/auth/ability';

interface Tile {
  to: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  group: string;
  /**
   * Ability gate. The Reports module is reachable by lots of roles (everyone
   * who has REPORTS:VIEW), but each tile maps to a specific underlying
   * module — a Teacher viewing /reports shouldn't see payroll register,
   * defaulters, or trial balance. We gate each tile on the *underlying*
   * subject so the hub auto-narrows itself per role.
   */
  can: { action: Actions; subject: Subjects };
}

const tiles: Tile[] = [
  // Finance
  {
    group: 'Finance',
    to: '/reports/cash-book',
    title: 'Cash book',
    description: 'Plain-language money in / money out — who paid, what was spent — with running balance. Printable.',
    icon: <WalletOutlined />,
    can: { action: 'view', subject: 'Finance' },
  },
  {
    group: 'Finance',
    to: '/reports/fee-collection',
    title: 'Fee collection summary',
    description: 'Receipts in a date range with breakdown by fee type and by day.',
    icon: <DollarOutlined />,
    can: { action: 'view', subject: 'Fee' },
  },
  {
    group: 'Finance',
    to: '/fee/defaulters',
    title: 'Fee defaulters',
    description: 'Outstanding fee invoices by class section.',
    icon: <AlertOutlined />,
    can: { action: 'view', subject: 'Fee' },
  },
  {
    group: 'Finance',
    to: '/reports/receivables-aging',
    title: 'Receivables aging',
    description: 'Open invoices bucketed by overdue days (current, 0-30, 31-60, 61-90, >90).',
    icon: <WalletOutlined />,
    can: { action: 'view', subject: 'Fee' },
  },
  {
    group: 'Finance',
    to: '/reports/concessions',
    title: 'Concessions (discounts & waivers)',
    description: 'Revenue forgone in a date range, broken down by fee type and discount type, with every waiver listed.',
    icon: <PercentageOutlined />,
    can: { action: 'view', subject: 'Fee' },
  },
  {
    group: 'Finance',
    to: '/reports/collection-trend',
    title: 'Collection vs billing trend',
    description: 'Per-month billed vs collected vs outstanding with a collection-efficiency %.',
    icon: <LineChartOutlined />,
    can: { action: 'view', subject: 'Fee' },
  },
  {
    group: 'Finance',
    to: '/reports/fee-type-yield',
    title: 'Fee-type yield',
    description: 'Budgeted (structures × headcount) vs billed vs collected, per fee type.',
    icon: <DollarOutlined />,
    can: { action: 'view', subject: 'Fee' },
  },
  {
    group: 'Finance',
    to: '/reports/backdated-receipts',
    title: 'Back-dated receipts',
    description: 'Receipts recorded against an older day than they were entered — flags cash that landed in an already-handed-over day.',
    icon: <AlertOutlined />,
    can: { action: 'view', subject: 'Finance' },
  },
  {
    group: 'Finance',
    to: '/finance/trial-balance',
    title: 'Trial balance',
    description: 'GL trial balance as of a given date.',
    icon: <BankOutlined />,
    can: { action: 'view', subject: 'Finance' },
  },
  {
    group: 'Finance',
    to: '/finance/daybook',
    title: 'Daybook',
    description: 'Every ledger entry in a date range, optionally filtered by account.',
    icon: <FileTextOutlined />,
    can: { action: 'view', subject: 'Finance' },
  },
  {
    group: 'Finance',
    to: '/finance/income-expenditure',
    title: 'Income & Expenditure',
    description: 'Period earnings vs operating costs, with net surplus / deficit.',
    icon: <DollarOutlined />,
    can: { action: 'view', subject: 'Finance' },
  },
  {
    group: 'Finance',
    to: '/finance/balance-sheet',
    title: 'Balance sheet',
    description: 'Assets vs liabilities + equity as of a given date.',
    icon: <BankOutlined />,
    can: { action: 'view', subject: 'Finance' },
  },
  {
    group: 'Finance',
    to: '/finance/bank-book',
    title: 'Cash & Bank book',
    description: 'Per-account chronological ledger with running balance.',
    icon: <FileTextOutlined />,
    can: { action: 'view', subject: 'Finance' },
  },
  {
    group: 'Finance',
    to: '/finance/reconciliation',
    title: 'Bank reconciliation',
    description: 'Tick off ledger entries against the bank statement.',
    icon: <BankOutlined />,
    can: { action: 'view', subject: 'Finance' },
  },
  // HR — payroll detail gated on StaffSalary (capability); list-only on Staff
  {
    group: 'HR',
    to: '/reports/payroll-register',
    title: 'Payroll register',
    description: 'Per-month list of every staff item with totals — print-friendly.',
    icon: <WalletOutlined />,
    can: { action: 'view', subject: 'StaffSalary' },
  },
  {
    group: 'HR',
    to: '/hr/attendance?view=month',
    title: 'Staff monthly attendance',
    description: 'Per-staff present / absent / late / half-day / leave counts for a month.',
    icon: <TeamOutlined />,
    can: { action: 'view', subject: 'Staff' },
  },
  // Inventory
  {
    group: 'Inventory',
    to: '/reports/low-stock',
    title: 'Low stock',
    description: 'Combined view of store items + consumables that are at or below reorder thresholds.',
    icon: <AlertOutlined />,
    can: { action: 'view', subject: 'Inventory' },
  },
  {
    group: 'Inventory',
    to: '/inventory/store?tab=outstanding',
    title: 'Store outstanding credit',
    description: 'Credit sales awaiting collection.',
    icon: <DollarOutlined />,
    can: { action: 'view', subject: 'Inventory' },
  },
  {
    group: 'Inventory',
    to: '/inventory/vendors/aging',
    title: 'Vendor aging',
    description: 'Outstanding payable bucketed 0-30 / 31-60 / 61-90 / 91+ days.',
    icon: <AlertOutlined />,
    can: { action: 'view', subject: 'Inventory' },
  },
  {
    group: 'Inventory',
    to: '/inventory/vendors/ledger',
    title: 'Vendor ledger',
    description: 'Per-vendor chronological purchases + payments with running payable.',
    icon: <FileTextOutlined />,
    can: { action: 'view', subject: 'Inventory' },
  },
  // Academics
  {
    group: 'Academics',
    to: '/academics/results',
    title: 'Class results',
    description: 'Per-section result list with calculate / publish actions and report-card drilldown.',
    icon: <BookOutlined />,
    can: { action: 'view', subject: 'Academics' },
  },
  {
    group: 'Academics',
    to: '/attendance/monthly',
    title: 'Student monthly attendance',
    description: 'Per-section student attendance summary for a month.',
    icon: <TeamOutlined />,
    can: { action: 'view', subject: 'Attendance' },
  },
  {
    group: 'Academics',
    to: '/reports/chronic-absentees',
    title: 'Chronic absentees',
    description: 'Students whose absence rate exceeds a configurable threshold over a date range.',
    icon: <AlertOutlined />,
    can: { action: 'view', subject: 'Attendance' },
  },
];

export function ReportsHubPage() {
  const { token } = antdTheme.useToken();
  const ability = useAbility();
  const visibleTiles = tiles.filter((t) => ability.can(t.can.action, t.can.subject));
  const groups = Array.from(new Set(visibleTiles.map((t) => t.group)));

  return (
    <div style={{ display: 'grid', gap: 24 }}>
      <PageHeader
        eyebrow="Module"
        title="Reports"
        description="Operational and financial reports across every module."
      />

      {visibleTiles.length === 0 && (
        <Empty description="No reports available for your role." />
      )}

      {groups.map((g) => (
        <div key={g} style={{ display: 'grid', gap: 12 }}>
          <Typography.Text strong style={{ fontSize: 13, letterSpacing: 0.5, textTransform: 'uppercase', opacity: 0.7 }}>
            {g}
          </Typography.Text>
          <Row gutter={[16, 16]}>
            {visibleTiles
              .filter((t) => t.group === g)
              .map((t) => (
                <Col key={t.to} xs={24} md={12} xl={8}>
                  <Link to={t.to} style={{ textDecoration: 'none' }}>
                    <Card
                      hoverable
                      className="lift-card"
                      style={{ height: '100%', borderColor: token.colorBorderSecondary }}
                      styles={{ body: { padding: 18 } }}
                    >
                      <div style={{ display: 'flex', gap: 12 }}>
                        <span
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 9,
                            display: 'grid',
                            placeItems: 'center',
                            background: token.colorFillTertiary,
                            color: token.colorPrimary,
                            fontSize: 16,
                            flexShrink: 0,
                          }}
                        >
                          {t.icon}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                            <Typography.Text strong style={{ fontSize: 14 }}>{t.title}</Typography.Text>
                            <RightOutlined style={{ fontSize: 11, color: token.colorTextTertiary }} />
                          </div>
                          <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                            {t.description}
                          </Typography.Text>
                        </div>
                      </div>
                    </Card>
                  </Link>
                </Col>
              ))}
          </Row>
        </div>
      ))}
    </div>
  );
}
